import { OrderAPI } from "@api/Order.api";
import SSBackgroundImage from "@core-components/backgroundImage/BackgroundImage";
import SSBgImageHeadline from "@core-components/backgroundImage/components/BgImageHeadline";
import ButtonVariants from "@core-components/button/ButtonVariants";
import Emoji from "@core-components/emoji/Emoji";
import ReviewBannerSmall from "@core-components/reviews/ReviewBannerSmall";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Group, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { AppNotification } from "@notifications/Notification";
import useUserStore from "@store/user.store";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import BasketAccountList from "./components/BasketAccountList";
import BasketEmailInput from "./components/BasketEmailInput";
import CreditCardPaymentMethod from "./components/CreditCardPaymentMethod";
import PaymentMethodAccepted from "./components/PaymentMethodAccepted";
import PaypalPaymentMethod from "./components/PaypalPaymentMethod";
import PriceDisplay from "./components/PriceDisplay";
import AffiliationCodeInput from "./components/codeInputs/AffiliationCodeInput";
import VoucherInput from "./components/codeInputs/VoucherInput";
import { EPaymentMethod } from "./models/EPaymentMethod";
import { useBasketStore } from "./stores/Basket.store";
import type { ICartItem } from "./stores/models/IBasketItem.models";
import type IBasketAccount from "./stores/models/IBasketItem.models";

const Basket: React.FC = () => {
  const { t } = useAppTranslation("pages.order.basket");
  const [invalidItems, setInvalidItems] = useState<ICartItem[]>([]);
  const firstSanitizeCheck = useRef(false);
  const { affiliationInformation } = useUserStore();
  const {
    clear: clearBasket,
    voucherInfo,
    setVoucherInfo,
    affiliationCodeInfo,
    setAffiliationCodeInfo,
    setEmail,
    email,
    setPaymentMethod,
    paymentMethod,
    accounts,
    setBasketAccounts,
    setPrice,
  } = useBasketStore();

  const basketForm = useForm({
    initialValues: {
      accounts,
      email: affiliationInformation?.email ?? email ?? "",
      affiliationCode: affiliationCodeInfo.code ?? "",
      voucher: voucherInfo.voucher ?? "",
      paymentMethod: paymentMethod,
      isOrderValid: false,
    },
  });

  const handleChangePaymentMethod = (method: EPaymentMethod) => {
    if (basketForm.getValues().paymentMethod === method) return;

    basketForm.setFieldValue("paymentMethod", method);

    sanitizeCart();
  };

  const handleDeleteAccount = (account: IBasketAccount) => {
    const { accounts } = basketForm.getValues();

    const filteredAccounts = accounts.filter(
      (oldAccount) => oldAccount.accountId !== account.accountId,
    );

    basketForm.setFieldValue("accounts", filteredAccounts);

    AppNotification.default({
      title: t("notifications.itemRemoved.title"),
      message: t("notifications.itemRemoved.message"),
    });

    sanitizeCart();
  };

  const handleClearAccounts = () => {
    basketForm.setFieldValue("accounts", []);

    AppNotification.default({
      title: t("notifications.basketEmptied.title"),
      message: t("notifications.basketEmptied.message"),
    });

    sanitizeCart();
  };

  const saveFormValues = (values: {
    email: string;
    affiliationCode: string;
    voucher: string;
    paymentMethod: EPaymentMethod;
    accounts: IBasketAccount[];
  }) => {
    setAffiliationCodeInfo({
      code: values.affiliationCode,
    });
    setVoucherInfo({
      voucher: values.voucher,
    });
    setEmail(values.email);
    setPaymentMethod(values.paymentMethod);

    setBasketAccounts(values.accounts);
  };

  /**
   * Save to local store all order information and sanitize accounts in cart
   */
  const sanitizeCart = () => {
    const formValues = basketForm.getValues();

    saveFormValues(formValues);

    OrderAPI.sanitizeCart({
      items: accounts,
      voucher: formValues.voucher.trim(),
      affiliationCode: formValues.affiliationCode.trim(),
      email: formValues.email.trim(),
      paymentMethod: formValues.paymentMethod,
    }).then((res) => {
      basketForm.resetDirty();

      setPrice(res.data.price);

      setAffiliationCodeInfo(res.data.affiliationCodeInfo);
      setVoucherInfo(res.data.voucherInfo);

      if (res.data.isValid) {
        return basketForm.setFieldValue("isOrderValid", true);
      }

      setInvalidItems(res.data.invalids);
      basketForm.setFieldValue("isOrderValid", false);
    });
  };

  const handlePay = () => {
    const { affiliationCode, email, paymentMethod, voucher, isOrderValid } =
      basketForm.getValues();

    if (!isOrderValid) return;

    OrderAPI.pay(accounts, email, paymentMethod, voucher, affiliationCode)
      .then((res) => {
        if (res.ok) {
          clearBasket();
          document.location.href = res.data.stripeCheckoutSessionUrl;
          return;
        }
        throw res.data;
      })
      .catch((err) => {
        if (err?.data?.errors) {
          basketForm.setFieldValue("isOrderValid", false);
          basketForm.resetDirty();

          AppNotification.error({
            title: t("notifications.orderError.title"),
            message: t("notifications.orderError.message"),
          });
        }
      });
  };

  useEffect(() => {
    if (affiliationCodeInfo.code !== basketForm.getValues().affiliationCode)
      basketForm.setFieldValue(
        "affiliationCode",
        affiliationCodeInfo.code ?? "",
      );
  }, [
    affiliationCodeInfo.code,
    basketForm.getValues,
    basketForm.setFieldValue,
  ]);

  useEffect(() => {
    if (firstSanitizeCheck.current) return;

    sanitizeCart();

    firstSanitizeCheck.current = true;
  }, []);

  return (
    <>
      <SSBackgroundImage>
        <SSBgImageHeadline>
          <Emoji.ShoppingCart size={45} />
          &nbsp;
          <SSWhiteText>{t("banner.title")}</SSWhiteText>
        </SSBgImageHeadline>
      </SSBackgroundImage>
      <Group justify="center" align="top" className="relative">
        <Stack className="w-[min(100%,500px)]">
          <BasketEmailInput
            {...basketForm.getInputProps("email")}
            sanitizeCart={sanitizeCart}
          />

          <Stack className="basket-container">
            <SSWhiteText className="font-bold font-secret-narrow text-sm">
              {t("paymentMethod.title")}
            </SSWhiteText>
            <CreditCardPaymentMethod
              isChosen={
                basketForm.getValues().paymentMethod ===
                EPaymentMethod.CREDIT_CARD
              }
              onChoose={handleChangePaymentMethod}
            />
            <PaypalPaymentMethod
              isChosen={
                basketForm.getValues().paymentMethod === EPaymentMethod.PAYPAL
              }
              onChoose={handleChangePaymentMethod}
            />
          </Stack>
        </Stack>
        <Stack className="w-[min(100%,500px)]">
          <ReviewBannerSmall />
          <Stack className="basket-container md:!p-9">
            <BasketAccountList
              accounts={basketForm.getValues().accounts}
              invalidAccounts={invalidItems}
              deleteAccount={handleDeleteAccount}
              clearAccounts={handleClearAccounts}
            />
            <Flex className="max-w-[500px] flex-wrap" gap={16}>
              <VoucherInput
                {...basketForm.getInputProps("voucher")}
                isDirty={basketForm.isDirty("voucher")}
                onValidate={sanitizeCart}
                isValid={voucherInfo.isValid}
              />
              <AffiliationCodeInput
                {...basketForm.getInputProps("affiliationCode")}
                isDirty={basketForm.isDirty("affiliationCode")}
                onValidate={sanitizeCart}
                isValid={affiliationCodeInfo.isValid}
              />
            </Flex>
            <PriceDisplay />
            <ButtonVariants.Gradient
              disabled={
                accounts.length <= 0 || !basketForm.getValues().isOrderValid
              }
              onClick={handlePay}
              classNames={{ root: "!h-[45px]" }}
              label={
                <>
                  <SSWhiteText
                    className={`!font-secret ${!basketForm.getValues().isOrderValid && "!text-primary-dark-text"} pr-2 font-extrabold uppercase`}
                  >
                    {t("securePayment")}
                  </SSWhiteText>
                  <IconArrowNarrowRight />
                </>
              }
            />
          </Stack>
          <PaymentMethodAccepted />
        </Stack>
      </Group>
    </>
  );
};

export default Basket;
